/**
 * RUN-044: turns an attribute's base value into its current one.
 *
 * The TypeScript twin of `nebulize_sdk.runtime.modifier`. Types are generated; this arithmetic
 * is not, and the two implementations must agree exactly.
 */
/** A value brought inside its declared bounds. Absent bounds do not constrain. */
export function clampAttribute(attribute, value) {
    let held = value;
    if (attribute.min !== undefined && attribute.min !== null) {
        held = Math.max(held, attribute.min);
    }
    if (attribute.max !== undefined && attribute.max !== null) {
        held = Math.min(held, attribute.max);
    }
    return held;
}
/** True when `candidate` outranks `held` as the winning override: higher order, then source. */
function outranks(candidate, held) {
    const candidateOrder = candidate.order ?? 0;
    const heldOrder = held.order ?? 0;
    if (candidateOrder !== heldOrder) {
        return candidateOrder > heldOrder;
    }
    return candidate.source > held.source;
}
/**
 * The current value: `(base + sum(add)) * product(multiply)`, then any override wins, clamped.
 *
 * An empty modifier set returns the clamped base exactly.
 */
export function evaluateAttribute(base, modifiers = []) {
    let added = 0;
    let multiplied = 1;
    let override;
    for (const one of modifiers) {
        if (one.op === "add") {
            added += one.magnitude;
        }
        else if (one.op === "multiply") {
            multiplied *= one.magnitude;
        }
        else if (override === undefined || outranks(one, override)) {
            override = one;
        }
    }
    const value = override !== undefined ? override.magnitude : (base.value + added) * multiplied;
    return clampAttribute(base, value);
}
/** The current value of a whole aggregate. */
export function currentValue(aggregate) {
    return evaluateAttribute(aggregate.base, aggregate.modifiers ?? []);
}
/** Every modifier except the ones one effect instance applied. */
export function retract(modifiers, source) {
    return modifiers.filter((one) => one.source !== source);
}
/** The set with `added` applied, replacing any modifier from the same source and op. */
export function applyModifier(modifiers, added) {
    const kept = modifiers.filter((one) => !(one.source === added.source && one.op === added.op));
    return [...kept, added];
}
